import { useEffect, useState } from "react";
import Dashboard from "../components/Dashboard";
import config from "../config";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  FioriSwitch,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
} from "../components/FioriUI";

const url = config.api.paymentReconciliation;

function PaymentReconciliation() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    exceptionType: "",
    search: "",
    customer: "",
    processedTime: "",
  });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsHtml, setDetailsHtml] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [shouldRefreshAfterWarning] = useState(false);
  const [rowToRefresh, setRowToRefresh] = useState(null);
  const [rowLoading, setRowLoading] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [exceptionTypeList, setExceptionTypeList] = useState([]);
  const [view, setView] = useState("");
  const [resolvedExceptions, setResolvedExceptions] = useState(new Set());
  const isDashboard = view === "dashboard";

  useEffect(() => {
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.exceptions || [];
        setData(apiData);
        setFilteredData(apiData);
        setLoading(false);
        setExceptionTypeList(
          Array.from(
            new Set(apiData.map((i) => i.exception_type).filter(Boolean)),
          ),
        );
        setCustomerList(
          Array.from(
            new Set(apiData.map((i) => i.provider_name).filter(Boolean)),
          ),
        );
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let filtered = [...data];
    if (filters.status) {
      filtered = filtered.filter(
        (item) =>
          item.exception_status &&
          item.exception_status.toUpperCase() === filters.status.toUpperCase(),
      );
    }
    if (filters.exceptionType) {
      filtered = filtered.filter(
        (item) => item.exception_type === filters.exceptionType,
      );
    }
    if (filters.customer) {
      filtered = filtered.filter(
        (item) => item.provider_name === filters.customer,
      );
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.exception_id?.toLowerCase().includes(s) ||
          item.abo_number?.toLowerCase().includes(s) ||
          item.provider_name?.toLowerCase().includes(s) ||
          item.exception_type?.toLowerCase().includes(s),
      );
    }
    setFilteredData(filtered);
  }, [data, filters]);

  useEffect(() => {
    if (shouldRefreshAfterWarning && !showWarning) window.location.reload();
  }, [shouldRefreshAfterWarning, showWarning]);

  const getValue = (row, key) => row[key] || "-";

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const handleClearFilters = () =>
    setFilters({
      status: "",
      exceptionType: "",
      search: "",
      customer: "",
      processedTime: "",
    });

  const handleRefreshData = () => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.exceptions || [];
        setData(apiData);
        setFilteredData(apiData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleResolve = async (exceptionId) => {
    setResolvedExceptions((prev) => new Set(prev).add(exceptionId));
    try {
      const response = await fetch(
        config.api.resolve,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ exception_id: exceptionId }),
        },
      );
      if (response.ok) {
        await response.json();
        setWarningMessage(
          "Resolution is in progress. Please check back in a few minutes.",
        );
        setShowWarning(true);
        setRowToRefresh(exceptionId);
      } else {
        setWarningMessage("Failed to resolve exception. Please try again.");
        setShowWarning(true);
        setResolvedExceptions((prev) => {
          const ns = new Set(prev);
          ns.delete(exceptionId);
          return ns;
        });
      }
    } catch (e) {
      setWarningMessage("Error resolving exception. Please try again.");
      setShowWarning(true);
      setResolvedExceptions((prev) => {
        const ns = new Set(prev);
        ns.delete(exceptionId);
        return ns;
      });
    }
  };

  const handleViewDetails = async (exceptionId) => {
    setDetailsLoading(true);
    setDetailsError("");
    setShowDetailsModal(true);
    try {
      const res = await fetch(url);
      const json = await res.json();
      let html = "";
      if (json.exceptions && Array.isArray(json.exceptions)) {
        const found = json.exceptions.find(
          (e) => e.exception_id === exceptionId,
        );
        if (found) {
          html = `
            <div style='margin-bottom:1rem;'>
              <div style='font-weight:600;margin-bottom:0.5rem;color:#1D2D3E;'>Exception Details</div>
              <div style='color:#556B82;'><strong>Resolution Notes:</strong> ${found.resolution_notes || "-"}</div>
            </div>`;
        } else {
          html =
            "<div style='padding:2rem;text-align:center;color:#A9B4BE;'>No details available for this exception.</div>";
        }
      }
      setDetailsHtml(html);
    } catch {
      setDetailsError("Failed to fetch details.");
      setDetailsHtml("");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleWarningClose = async () => {
    setShowWarning(false);
    if (rowToRefresh) {
      setRowLoading(rowToRefresh);
      const res = await fetch(url);
      const json = await res.json();
      const apiData = json.exceptions || [];
      const updated = apiData.find((i) => i.exception_id === rowToRefresh);
      if (updated) {
        setData((prev) =>
          prev.map((i) => (i.exception_id === rowToRefresh ? updated : i)),
        );
        setFilteredData((prev) =>
          prev.map((i) => (i.exception_id === rowToRefresh ? updated : i)),
        );
      }
      setRowToRefresh(null);
      setRowLoading(null);
    }
  };

  return (
    <>
      <ObjectPageHeader
        title="Payment Reconciliation"
        subtitle="Resolve exceptions raised during payment matching"
        actions={
          <>
            <FioriSwitch
              checked={isDashboard}
              onChange={(v) => setView(v ? "dashboard" : "")}
              label="Dashboard"
            />
            <FioriButton
              variant="emphasized"
              onClick={handleRefreshData}
              disabled={loading}
              loading={loading}
            >
              {loading ? "Refreshing" : "Refresh"}
            </FioriButton>
          </>
        }
      />

      <PageContainer>
        <FilterBar
          title="Filter Exceptions"
          onClear={handleClearFilters}
          footer={
            <SearchInput
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search exception ID, ABO number, provider, or type"
            />
          }
        >
          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All</option>
              <option value="AWAITING_RESPONSE">Awaiting Response</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
            </Select>
          </Field>
          <Field label="Exception Type" htmlFor="reasonCode">
            <Select
              id="reasonCode"
              value={filters.exceptionType}
              onChange={(e) =>
                handleFilterChange("exceptionType", e.target.value)
              }
            >
              <option value="">All</option>
              {exceptionTypeList.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Provider" htmlFor="provider">
            <Select
              id="provider"
              value={filters.customer}
              onChange={(e) => handleFilterChange("customer", e.target.value)}
            >
              <option value="">All</option>
              {customerList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </FilterBar>

        {isDashboard ? (
          <Dashboard filteredData={filteredData} source="payment" />
        ) : (
          <ResponsiveTable>
            <thead>
              <tr>
                <th>Exception ID</th>
                <th>ABO Number</th>
                <th>Provider</th>
                <th className="whitespace-nowrap">Expected</th>
                <th className="whitespace-nowrap">Received</th>
                <th>Exception Type</th>
                <th>Status</th>
                <th className="text-center">Details</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9}>
                    <div className="flex justify-center items-center gap-3 py-8 text-fiori-text-secondary text-sm">
                      <Spinner /> Loading exceptions
                    </div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState
                      title="No exceptions"
                      description="No items match the current filters."
                    />
                  </td>
                </tr>
              ) : (
                filteredData.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <span
                        className="font-mono text-xs truncate inline-block max-w-[140px] align-middle"
                        title={getValue(row, "exception_id")}
                      >
                        {getValue(row, "exception_id")}
                      </span>
                    </td>
                    <td>{getValue(row, "abo_number")}</td>
                    <td>{getValue(row, "provider_name")}</td>
                    <td className="whitespace-nowrap font-mono">
                      {row.expected_amount
                        ? `${row.expected_amount.toLocaleString()} ${row.currency || ""}`
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap font-mono">
                      {row.received_amount
                        ? `${row.received_amount.toLocaleString()} ${row.currency || ""}`
                        : "-"}
                    </td>
                    <td>{getValue(row, "exception_type")}</td>
                    <td>
                      <StatusPill status={row.exception_status} />
                    </td>
                    <td className="text-center">
                      {!["OPEN", "INITIATED"].includes(
                        (row.exception_status || "").toUpperCase(),
                      ) && (
                        <IconButton
                          title="View Details"
                          onClick={() => handleViewDetails(row.exception_id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"
                            />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </IconButton>
                      )}
                    </td>
                    <td className="text-right">
                      {rowLoading === row.exception_id ? (
                        <Spinner size="sm" />
                      ) : (
                        <FioriButton
                          variant="emphasized"
                          onClick={() => handleResolve(row.exception_id)}
                          disabled={resolvedExceptions.has(row.exception_id)}
                        >
                          Resolve
                        </FioriButton>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        )}
      </PageContainer>

      {/* Status confirmation dialog */}
      <Dialog
        show={showWarning}
        title="Successfully Initiated"
        onClose={handleWarningClose}
        size="sm"
        footer={
          <FioriButton variant="emphasized" onClick={handleWarningClose}>
            OK
          </FioriButton>
        }
      >
        <div className="px-4 py-5 text-sm text-fiori-text">
          {warningMessage}
        </div>
      </Dialog>

      {/* Details dialog */}
      <Dialog
        show={showDetailsModal}
        title="Action History"
        onClose={() => setShowDetailsModal(false)}
        size="lg"
        footer={
          <FioriButton onClick={() => setShowDetailsModal(false)}>
            Close
          </FioriButton>
        }
      >
        <div className="p-4">
          {detailsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[150px] gap-3">
              <Spinner size="lg" />
              <p className="text-sm text-fiori-text-secondary">Loading...</p>
            </div>
          ) : detailsError ? (
            <p className="text-center text-fiori-error py-6">{detailsError}</p>
          ) : (
            <div
              className="text-sm text-fiori-text"
              dangerouslySetInnerHTML={{ __html: detailsHtml }}
            />
          )}
        </div>
      </Dialog>
    </>
  );
}

export default PaymentReconciliation;
 